import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';

// Campanella notifiche riutilizzabile (stessa sorgente dati dell'header NLT).
// accent: 'emerald' | 'blue' — colore dell'header del dropdown e degli evidenziatori.
// modulo: se valorizzato (es. 'bici'), mostra SOLO le notifiche di quel modulo (datiAggiuntivi.modulo).
export default function NotificheBell({ user, accent = 'emerald', modulo = null }) {
  const [notifiche, setNotifiche] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const A = accent === 'blue'
    ? { grad: 'from-blue-500 to-blue-600', unread: 'bg-blue-50', dot: 'bg-blue-500', sub: 'text-blue-100' }
    : { grad: 'from-emerald-500 to-teal-600', unread: 'bg-emerald-50', dot: 'bg-emerald-500', sub: 'text-emerald-100' };

  const fetchNotifiche = useCallback(async () => {
    try {
      const response = await api.get('/notifiche');
      if (response.data && response.data.success) {
        const data = response.data.data || {};
        let tutte = [...(data.scadenzeOfferte || []), ...(data.pratiche || [])];
        // Differenzia per modulo: se richiesto, tieni solo quelle del modulo indicato
        if (modulo) tutte = tutte.filter((n) => n.datiAggiuntivi?.modulo === modulo);
        tutte.sort((a, b) => new Date(b.dataNotifica || 0) - new Date(a.dataNotifica || 0));
        setNotifiche(tutte);
      }
    } catch (e) { /* silenzioso */ }
  }, [modulo]);

  useEffect(() => {
    if (!user) return undefined;
    fetchNotifiche();
    const interval = setInterval(fetchNotifiche, 10000);
    const onFocus = () => fetchNotifiche();
    const onVis = () => { if (document.visibilityState === 'visible') fetchNotifiche(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user, fetchNotifiche]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const nonLette = notifiche.filter((n) => !n.letta).length;

  const marcaLetta = async (id) => {
    try { await api.put(`/notifiche/${id}/letta`); } catch (e) { /* ignora */ }
    setNotifiche((prev) => prev.map((n) => (n.id === id ? { ...n, letta: true } : n)));
  };

  const marcaTutteLette = async () => {
    try { await api.put('/notifiche/marca-tutte-lette'); } catch (e) { /* ignora */ }
    setNotifiche((prev) => prev.map((n) => ({ ...n, letta: true })));
  };

  const onItem = async (n) => {
    if (!n.letta && n.id) await marcaLetta(n.id);
    if (n.link) navigate(n.link);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifiche"
      >
        <BellIcon className="h-5 w-5 sm:h-6 sm:w-6" />
        {nonLette > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 sm:h-5 sm:w-5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 sm:h-5 sm:w-5 bg-red-500 items-center justify-center text-white text-[10px] sm:text-xs font-bold">
              {nonLette > 9 ? '9+' : nonLette}
            </span>
          </span>
        )}
      </button>

      {open && (
        <div className="origin-top-right absolute right-0 mt-2 w-80 sm:w-96 rounded-xl shadow-2xl bg-white ring-1 ring-black ring-opacity-5 z-50 max-h-[32rem] overflow-hidden">
          <div className={`px-4 py-3 bg-gradient-to-r ${A.grad} flex items-center justify-between`}>
            <div>
              <h3 className="text-sm font-semibold text-white">Notifiche</h3>
              {nonLette > 0 && (
                <p className={`text-xs ${A.sub} mt-0.5`}>{nonLette} {nonLette === 1 ? 'nuova notifica' : 'nuove notifiche'}</p>
              )}
            </div>
            {nonLette > 0 && (
              <button onClick={marcaTutteLette} className="text-xs text-white/90 hover:text-white underline">Segna tutte lette</button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifiche.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <BellIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Nessuna notifica</p>
                <p className="text-xs text-gray-400 mt-1">Sei aggiornato su tutto!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifiche.map((n, i) => (
                  <div key={n.id || i}
                    className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${!n.letta ? A.unread : ''}`}
                    onClick={() => onItem(n)}>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className={`h-2 w-2 rounded-full ${!n.letta ? A.dot : 'bg-gray-300'}`}></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{n.titolo}</p>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{n.descrizione || n.messaggio}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {n.dataNotifica ? new Date(n.dataNotifica).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
